import { Element } from "@xmpp/xml"

export type AccountChat = {
  jid: string
  name: string
  group: string
  groupName: string
  groupDescriptions: string[]
  verified: boolean
  hidden: boolean
  driverA: string | undefined
  driverB: string | undefined
  carId: string | undefined
  tmsCarId?: string
  tmsTrailerId?: string
  whatsappInboundRequired?: boolean
}

export const ACCOUNT_CHAT_NS = "urn:slickshift:account:chats:0"
export const USER_CHAT_NS = "urn:slickshift:account:user-chats:0"
const DEFAULT_CHAT_GROUP_MAPPING = {
  automation: "Groups",
  conference: "Groups",
  default: "Groups",
  driver: "Drivers",
  driver_muc: "Drivers",
  shared_driver: "Shared Drivers",
  external: "Whatsapp Contacts",
  double_crew: "Double Crew",
  transics_driver: "Transics",
  platform_science_driver: "Platform Science",
}

type GroupKeys = keyof typeof DEFAULT_CHAT_GROUP_MAPPING

type ParseOptions = {
  configElementName?: "account-chat-config" | "user-chat-config"
  loggedInUserJidBare?: string
}

const isSlickshiftBareJid = (jidBare?: string): boolean => {
  if (!jidBare) return false
  const [local] = jidBare.toLowerCase().split("@")
  return local === "slickshift"
}

export default class AccountChatsParser {
  static parse(stanza: Element, options: ParseOptions = {}): AccountChat[] {
    const items = stanza.getChild("pubsub")?.getChild("items")
    if (!items) return []

    const chatOrNones = items
      .getChildren("item")
      .map((item) => this.parseItem(item, options))

    return chatOrNones.filter((chat) => chat !== undefined) as AccountChat[]
  }

  static parseItem(
    item: Element,
    options: ParseOptions = {},
  ): AccountChat | undefined {
    const configElementName = options.configElementName || "account-chat-config"
    const chatConfig = item.getChild(configElementName)

    if (!chatConfig) {
      throw new Error(`${configElementName} not found`)
    }

    const loggedInUserJidBare = options.loggedInUserJidBare?.toLowerCase()
    if (loggedInUserJidBare && !isSlickshiftBareJid(loggedInUserJidBare)) {
      const allowedUsers =
        chatConfig
          .getAttr("allowed_users")
          ?.split(",")
          .map((user: string) => user.trim().toLowerCase())
          .filter((user: string) => user !== "") || []

      if (!allowedUsers.includes(loggedInUserJidBare)) {
        return undefined
      }
    }

    let groupConfig = chatConfig.getAttr("group_config")
    if (!groupConfig || groupConfig === "" || groupConfig === " ") {
      groupConfig = ""
    }

    const group = chatConfig.getAttr("group")

    const groupName =
      group in DEFAULT_CHAT_GROUP_MAPPING
        ? DEFAULT_CHAT_GROUP_MAPPING[group as GroupKeys]
        : group

    const driverA = chatConfig.getAttr("driver_a")
    const driverB = chatConfig.getAttr("driver_b")
    const carId = chatConfig.getAttr("car_id")
    const tmsCarId = chatConfig.getAttr("tms_car_id")
    const tmsTrailerId = chatConfig.getAttr("tms_trailer_id")
    const whatsappInboundRequiredRaw = chatConfig.getAttr(
      "whatsapp_inbound_required",
    )
    const whatsappInboundRequired =
      whatsappInboundRequiredRaw != null
        ? !["false", "0", ""].includes(whatsappInboundRequiredRaw.toLowerCase())
        : undefined

    return {
      jid: item.getAttr("id"),
      name: chatConfig.getAttr("name"),
      group,
      groupName,
      groupDescriptions: groupConfig !== "" ? groupConfig.split(",") : [],
      verified: chatConfig.getAttr("verified") === "1",
      hidden: chatConfig.getAttr("hidden") === "1",
      driverA: driverA,
      driverB: driverB,
      carId: carId,
      tmsCarId: tmsCarId,
      tmsTrailerId: tmsTrailerId,
      whatsappInboundRequired,
    }
  }
}
